import type {DefaultError} from "@tanstack/react-query";
import {type UrlQueryKey, useCustomQuery} from "../../query/queryClient";
import type {PtSupplier, PtTermBucket, PtTermMapping, PtTrendPoint} from "./paymentTermsDemoData";

/** `GET /api/showcase/payment_terms/trend/` */
export const PAYMENT_TERMS_TREND_URL = "/showcase/payment_terms/trend/";

/** `GET /api/showcase/payment_terms/buckets/` */
export const PAYMENT_TERMS_BUCKETS_URL = "/showcase/payment_terms/buckets/";

/** `GET /api/showcase/payment_terms/mapping/` */
export const PAYMENT_TERMS_MAPPING_URL = "/showcase/payment_terms/mapping/";

/** `GET /api/showcase/payment_terms/suppliers/` */
export const PAYMENT_TERMS_SUPPLIERS_URL = "/showcase/payment_terms/suppliers/";

export const PAYMENT_TERMS_TREND_QUERY_KEY = [PAYMENT_TERMS_TREND_URL] as const satisfies UrlQueryKey;
export const PAYMENT_TERMS_BUCKETS_QUERY_KEY = [PAYMENT_TERMS_BUCKETS_URL] as const satisfies UrlQueryKey;
export const PAYMENT_TERMS_MAPPING_QUERY_KEY = [PAYMENT_TERMS_MAPPING_URL] as const satisfies UrlQueryKey;
export const PAYMENT_TERMS_SUPPLIERS_QUERY_KEY = [PAYMENT_TERMS_SUPPLIERS_URL] as const satisfies UrlQueryKey;

type ListResponse<T> = {list: T[]};
type SuppliersResponse = {suppliers: PtSupplier[]};

/** Month-by-month spend-weighted average payment term. */
export function usePaymentTermsTrendQuery() {
    return useCustomQuery<
        ListResponse<PtTrendPoint>,
        DefaultError,
        PtTrendPoint[],
        typeof PAYMENT_TERMS_TREND_QUERY_KEY
    >({
        queryKey: PAYMENT_TERMS_TREND_QUERY_KEY,
        select: data => data.list ?? [],
    });
}

/** Spend buckets on the payment-term ladder. */
export function usePaymentTermsBucketsQuery() {
    return useCustomQuery<
        ListResponse<PtTermBucket>,
        DefaultError,
        PtTermBucket[],
        typeof PAYMENT_TERMS_BUCKETS_QUERY_KEY
    >({
        queryKey: PAYMENT_TERMS_BUCKETS_QUERY_KEY,
        select: data => data.list ?? [],
    });
}

/** Raw ERP payment-term texts normalized to net days. */
export function usePaymentTermsMappingQuery() {
    return useCustomQuery<
        ListResponse<PtTermMapping>,
        DefaultError,
        PtTermMapping[],
        typeof PAYMENT_TERMS_MAPPING_QUERY_KEY
    >({
        queryKey: PAYMENT_TERMS_MAPPING_QUERY_KEY,
        select: data => data.list ?? [],
    });
}

/** Supplier-level payment-term opportunities. */
export function usePaymentTermsSuppliersQuery() {
    return useCustomQuery<
        SuppliersResponse,
        DefaultError,
        PtSupplier[],
        typeof PAYMENT_TERMS_SUPPLIERS_QUERY_KEY
    >({
        queryKey: PAYMENT_TERMS_SUPPLIERS_QUERY_KEY,
        select: data => data.suppliers ?? [],
    });
}
